import { Config } from 'config/envConfig';
import React, { useRef, useState } from 'react';
import {
    faM,
    faPaperPlane,
    faFileAlt,
    faTimes,
    faPlus,
} from '@fortawesome/free-solid-svg-icons';

import { alert, AlertType, Button, Input, Dropdown } from '@quyva/shared-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RepoType } from '@quyva/common';
import axios from 'axios';
import { getUrl } from '../pdfDisplay/utils';
import { validateUploadedFiles } from '../../utils/validateUploadedFiles';

export const ChatActions = ({
    query,
    fineTunedMode,
    repoName,
    setQuery,
    handleFineTune,
    handleSubmit,
    handleRegenerate,
    currentQnA,
    isGenerating,
    fileToChat,
    isDatabase,
    processingRepos,
    repoId,
    attachmentsList,
    setAttachmentsList,
    uploadedFiles,
    setUploadedFiles,
    repository,
    hasDefaultVirtualRepo,
    virtualRepos,
    selectedVirtualRepoId,
    setSelectedVirtualRepoId,
    onVirtualRepoSelected,
    formPending,
}) => {
    const buttonRef = useRef(null);
    const fileInputRef = useRef(null); // For triggering the upload dialog
    const [disableRegenerate, setDisableRegenerate] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            buttonRef?.current?.click(); // Programmatically click the button
        }
    };
    // Checking if any operation is going on this repo.
    const isProcessingOnRepo = processingRepos.find(
        (task) => task.repoId === repoId
    );
    // File Upload Logic (Inline)
    const onFileUpload = async (files) => {
        setUploading(true);

        try {
            const { errors, validFiles } = await validateUploadedFiles(
                files,
                uploadedFiles
            );

            if (errors.length > 0) {
                alert(AlertType.ERROR, errors.join('\n'));
                return;
            }

            if (validFiles.length > 0) {
                setUploadedFiles((prev) => [...prev, ...validFiles]);
            }
        } catch (err) {
            console.error('Upload error:', err);
            alert(AlertType.ERROR, 'An error occurred during file upload.');
        } finally {
            setUploading(false);
        }
    };

    //  Triggered when user selects files
    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        await onFileUpload(files);
        // reset input so user can re-select same files if needed
        e.target.value = null;
    };

    // Remove a local uploaded file by index
    const handleRemoveUploadedFile = (index) => {
        setUploadedFiles((prevFiles) =>
            prevFiles.filter((_, i) => i !== index)
        );
    };

    // Remove a remote attachment (from attachmentsList) by index (index relative within attachmentsList)
    const handleRemoveRemoteAttachment = (index) => {
        if (!setAttachmentsList) return;
        if (!Array.isArray(attachmentsList)) return;
        const updated = attachmentsList.filter((_, i) => i !== index);
        setAttachmentsList(updated);
    };

    // Combined display array:
    // - First map attachmentsList (remote) -> { type: 'remote', name, originalPath, idx }
    // - Then uploadedFiles (local) -> { type: 'local', file, name, idx }
    const buildCombinedList = () => {
        const remote = Array.isArray(attachmentsList)
            ? attachmentsList.map((p, i) => ({
                  type: 'remote',
                  name:
                      typeof p === 'string'
                          ? p.split(/[/\\]/).pop()
                          : `file-${i + 1}`,
                  path: p,
                  index: i, // index in attachmentsList
              }))
            : [];

        const local = uploadedFiles.map((f, i) => ({
            type: 'local',
            name: f.name,
            file: f,
            index: i, // index in uploadedFiles
        }));

        return [...remote, ...local];
    };

    // Sending query + attached files (both remote attachmentsList and local uploadedFiles)
    const handleSend = async () => {
        if (!query.trim()) return;

        try {
            await handleSubmit(false, selectedVirtualRepoId);
            setQuery('');
            setUploadedFiles([]);
            setDisableRegenerate(false);
        } catch (err) {
            console.error('handleSend error:', err);
        }
    };

    // helper to render remove button per item
    const renderRemoveBtn = (item) => {
        if (item.type === 'remote') {
            return (
                <button
                    onClick={() => handleRemoveRemoteAttachment(item.index)}
                    className='text-texts-secondary300 hover:text-texts-red400'
                    title='Remove file'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            );
        } else {
            return (
                <button
                    onClick={() => handleRemoveUploadedFile(item.index)}
                    className='text-texts-secondary300 hover:text-texts-red400'
                    title='Remove file'
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            );
        }
    };

    const combinedList = buildCombinedList();

    const getTruncatedVirtualRepoLabel = (name) => {
        const maxLength = 15;
        if (!name) return '';
        return name.length > maxLength
            ? `${name.slice(0, maxLength)}...`
            : name;
    };

    const hasBackendVirtualRepos =
        Array.isArray(virtualRepos) && virtualRepos.length > 0;

    const handleSelectVirtualRepo = async (virtualRepoId) => {
        // Update local state immediately for snappy UI
        setSelectedVirtualRepoId(virtualRepoId);
        try {
            if (onVirtualRepoSelected) {
                onVirtualRepoSelected(virtualRepoId);
            }
        } catch (e) {
            // ignore
        }

        if (!hasBackendVirtualRepos || !repoId) return;

        try {
            const baseUrl = `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}`;
            const url = getUrl(
                `${baseUrl}/virtual_repo_routes/set-default-virtual-repo`
            );

            await axios.put(url, {
                parent_repo_id: repoId,
                virtual_repo_id: virtualRepoId,
            });
        } catch (e) {
            console.log('set-default-virtual-repo api called.');
            alert(
                AlertType.ERROR,
                'Failed to update default virtual repository. Your selection will apply for this session only.'
            );
        }
    };

    // Build dropdown items: a DEFAULT option plus all backend virtual repos by name
    const virtualRepoDropdownItems = hasBackendVirtualRepos
        ? [
              {
                  label: 'DEFAULT',
                  selected: selectedVirtualRepoId == null,
                  action: () => handleSelectVirtualRepo(null),
              },
              ...virtualRepos.map((item) => ({
                  label:
                      item.virtual_repo_name ||
                      `Config ${item.virtual_repo_id}`,
                  selected: selectedVirtualRepoId === item.virtual_repo_id,
                  action: () => handleSelectVirtualRepo(item.virtual_repo_id),
              })),
          ]
        : [];

    const selectedVirtualRepoLabel = hasBackendVirtualRepos
        ? getTruncatedVirtualRepoLabel(
              selectedVirtualRepoId == null
                  ? 'DEFAULT'
                  : virtualRepos.find(
                        (item) => item.virtual_repo_id === selectedVirtualRepoId
                    )?.virtual_repo_name || `Config ${selectedVirtualRepoId}`
          )
        : '';

    return (
        <div className='w-full mx-3'>
            <div className='flex items-center justify-between'>
                {/* + Upload button */}
                {repository?.repotype === RepoType.MASTER_AGENT && (
                    <>
                        <Button
                            className='w-12 h-12 ml-1 mr-1 bg-backgrounds-primary'
                            icon={faPlus}
                            type='success'
                            hoverText='Upload a document or image'
                            onClick={() => fileInputRef.current?.click()}
                        />

                        <input
                            type='file'
                            ref={fileInputRef}
                            multiple
                            accept='.pdf,.doc,.docx,.png,.jpg,.jpeg'
                            className='hidden'
                            onChange={handleFileChange}
                        />
                    </>
                )}

                {/* Textbox with inline file preview */}
                <div
                    className='w-full bg-backgrounds-white border border-1 rounded-xl px-3 py-2 flex flex-col'
                    style={{ minHeight: '2rem' }}
                >
                    {/* Inline file preview area */}
                    {combinedList.length > 0 && (
                        <div className='flex flex-wrap gap-2 mb-2'>
                            {combinedList.map((item, index) => (
                                <div
                                    key={`${item.type}-${item.index}-${index}`}
                                    className='flex items-center gap-1 bg-backgrounds-secondary text-xs border bg-backgrounds-border rounded-lg px-2 py-1'
                                >
                                    <FontAwesomeIcon
                                        icon={faFileAlt}
                                        className='text-texts-tertiary'
                                    />
                                    <span className='truncate max-w-[120px]'>
                                        {item.name}
                                    </span>
                                    {renderRemoveBtn(item)}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 🔹 Input field */}
                    <Input
                        className='px-2 py-2 w-full border-none focus:ring-0 focus:outline-none'
                        value={query}
                        type='text'
                        placeholder={
                            !fineTunedMode
                                ? `Ask question from ${
                                      fileToChat === '' ||
                                      fileToChat === undefined
                                          ? repoName
                                          : fileToChat
                                  }'s model here`
                                : `Ask question from fine tuned model of ${repoName}'s here`
                        }
                        onChange={(e) => {
                            setQuery(e.target.value);
                        }}
                        autoFocus={true}
                        onKeyDown={handleKeyDown}
                        disabled={isProcessingOnRepo || formPending}
                    />
                </div>
                {/* Virtual repository selector (shown only when a default virtual repo exists) and send button */}
                <div className='flex items-center ml-2'>
                    {hasDefaultVirtualRepo && (
                        <Dropdown
                            dropDownList={virtualRepoDropdownItems}
                            showButtonText={true}
                            buttonText={selectedVirtualRepoLabel}
                            buttonHoverText='Virtual repository configuration'
                            buttonClassName='max-w-[10rem] px-2 h-8 flex items-center justify-center border border-backgrounds-border rounded-lg text-[11px] text-texts-secondary300 truncate mr-1'
                            placement='bottomRight'
                        />
                    )}

                    {isDatabase == null &&
                    (fileToChat === '' || fileToChat === undefined) ? (
                        <div className='flex items-center justify-between'>
                            <Button
                                ref={buttonRef}
                                className='w-9 h-9 ml-1'
                                hoverText={
                                    isProcessingOnRepo
                                        ? 'Wait for the files to process before chatting.'
                                        : 'Send question and get an answer'
                                }
                                disabled={
                                    query.length === 0 ||
                                    isGenerating ||
                                    isProcessingOnRepo ||
                                    formPending
                                }
                                onClick={
                                    fineTunedMode
                                        ? handleFineTune
                                        : async () => {
                                              await handleSend();
                                              setDisableRegenerate(false);
                                              setQuery('');
                                          }
                                }
                                icon={faPaperPlane}
                                type={'success'}
                            />
                        </div>
                    ) : (
                        <div className='flex items-center justify-between'>
                            <Button
                                ref={buttonRef}
                                className='w-9 h-9 ml-1 mr-1'
                                hoverText='Send question and get single answer'
                                disabled={
                                    query.length === 0 ||
                                    isGenerating ||
                                    formPending
                                }
                                onClick={
                                    fineTunedMode
                                        ? handleFineTune
                                        : async () => {
                                              await handleSend();
                                              setDisableRegenerate(false);
                                              setQuery('');
                                          }
                                }
                                icon={faPaperPlane}
                                type={'success'}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
